// Generic card-event router (cards_version 3). When sam_cards.client_event_routing is on, SAM
// workspace envelopes are routed on `card_events` (type + card id + args) for the cards below,
// instead of matching legacy action names. Everything this router does not know — workflow
// surfaces, quick quote, drafts, note capture, proposal/calendar commands, all card.signal commands, view control — stays on
// the legacy per-action path, so turning the flag on can only change the known standard cards.
//
// Behaviour is kept identical to the legacy branches in useSamActions for each handled case
// (same handlers, same order within the envelope, same once-per-module apply, same workflow-turn
// suppression), so the two paths are interchangeable per envelope.
import type { CardDefinition } from '@/redux/internal-services/agentApi';
import type { SamAction, SamCardEvent, SamFacet, SamModule } from '@/components/SAM/samActionTypes';

export interface CardEventHandlers {
  onOpenCard?: (module: SamModule | undefined, recordId: string | null, index?: number) => void;
  onApplyFilter?: (module: SamModule, filter: Record<string, unknown>, facets?: SamFacet[], title?: string,
    caption?: string, messageId?: string, recordId?: string | null, view?: { title?: string; caption?: string }) => void;
  onActionCard?: (module: SamModule, filter: Record<string, unknown>, title?: string, caption?: string, recordId?: string | null) => void;
  onRefresh?: (module: SamModule, messageId?: string) => void;
  onRefreshTaskDetail?: (taskId: string) => void;
  onRefreshMeetingDetail?: (meetingId: string) => void;
  onRefreshPropertyDetail?: (propertyId: string) => void;
  onRefreshTaskComments?: (taskId: string) => void;
}

export interface CardRouteContext {
  messageId?: string;
  /** Modules already applied in this envelope (legacy fetches each module once per envelope). */
  applied: Set<string>;
  /** True when this turn already claimed the Workflow surface (legacy workflowTurnIdsRef). */
  suppressSurfaceSwitch: (module: string) => boolean;
  /** Card trace: an event the router deliberately did not apply (and why). */
  onIgnored?: (ev: SamCardEvent, reason: string) => void;
}

const DETAIL_REFRESH: Record<string, keyof CardEventHandlers> = {
  REFRESH_TASK_DETAIL: 'onRefreshTaskDetail',
  REFRESH_MEETING_DETAIL: 'onRefreshMeetingDetail',
  REFRESH_PROPERTY_DETAIL: 'onRefreshPropertyDetail',
  REFRESH_TASK_COMMENTS: 'onRefreshTaskComments',
};
// Legacy handles these literally (not via the generic REFRESH_<module> path) — leave them there.
const LEGACY_ONLY_REFRESH = new Set(['REFRESH_QUICK_QUOTE', 'REFRESH_WORKFLOW_SETTINGS', 'REFRESH_WORKFLOW', 'REFRESH_BILLING', 'REFRESH_JOB_CARD', 'REFRESH_SITE_BOARD']);

const isListOpen = (t: string) => t.endsWith('_PANEL') || t.includes('FILTER');
const isDetailOpen = (t: string) => t.startsWith('OPEN_') && t.includes('DETAIL');
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

/** Whether this router can handle the event on its own (else the legacy path must). */
export function canRouteCardEvent(ev: SamCardEvent, card: CardDefinition | undefined): boolean {
  if (!card || card.module === 'workflow') return false;
  const types = ev.action_types;
  if (ev.type === 'card.open') {
    if (card.presentation === 'list' || card.presentation === 'panel') return types.every(isListOpen);
    if (card.presentation === 'view') return types.every(isDetailOpen);
    return false;
  }
  if (ev.type === 'card.refresh') return types.every(t => t.startsWith('REFRESH_') && !LEGACY_ONLY_REFRESH.has(t));
  return false;
}

/** Route one supported event. Call only after canRouteCardEvent() returned true. */
export function routeCardEvent(ev: SamCardEvent, card: CardDefinition, h: CardEventHandlers, ctx: CardRouteContext): void {
  const module = card.module as SamModule;
  const args = ev.args ?? {};
  const recordId = str(args.record_id) ?? ev.record_id ?? null;
  if (ev.type === 'card.open' && card.presentation === 'view') {
    h.onOpenCard?.(module, recordId, typeof args.index === 'number' ? args.index : undefined);
    return;
  }
  if (ev.type === 'card.open') {
    if (ctx.suppressSurfaceSwitch(module)) {
      ctx.onIgnored?.(ev, 'legacy_rule:workflowTurnIdsRef');
      return;
    }
    if (ctx.applied.has(module)) return;
    ctx.applied.add(module);
    const filter = (args.filter as Record<string, unknown> | undefined) ?? {};
    h.onOpenCard?.(undefined, null);
    h.onApplyFilter?.(module, filter, args.facets as SamFacet[] | undefined, ev.title ?? undefined, ev.caption ?? undefined,
      ctx.messageId, recordId, { title: ev.view_title ?? undefined, caption: ev.view_caption ?? undefined });
    if (ev.title || ev.caption) h.onActionCard?.(module, filter, ev.title ?? undefined, ev.caption ?? undefined, recordId);
    return;
  }
  if (ev.type === 'card.refresh') {
    for (const t of ev.action_types) {
      const detail = DETAIL_REFRESH[t];
      if (detail) {
        if (recordId) (h[detail] as ((id: string) => void) | undefined)?.(recordId);
      } else if (!ctx.suppressSurfaceSwitch(module)) {
        h.onRefresh?.(module, ctx.messageId);
      } else {
        ctx.onIgnored?.(ev, 'legacy_rule:workflowTurnIdsRef');
      }
    }
  }
}

export type EnvelopeStep = { kind: 'event'; event: SamCardEvent; card: CardDefinition } | { kind: 'action'; action: SamAction };

/** Walk an envelope in ORIGINAL action order: each routable card event is emitted once, at the
 *  position of its first action; every other action stays a legacy step. */
export function planEnvelope(
  actions: SamAction[], events: SamCardEvent[] | undefined, catalogue: CardDefinition[] | undefined,
): EnvelopeStep[] {
  const byId = new Map((catalogue ?? []).map(c => [c.id, c]));
  const routable = new Map<string, { event: SamCardEvent; card: CardDefinition }>();
  for (const ev of events ?? []) {
    const card = byId.get(ev.card_definition_id);
    if (canRouteCardEvent(ev, card)) routable.set(ev.card_definition_id, { event: ev, card: card! });
  }
  const emitted = new Set<string>();
  const steps: EnvelopeStep[] = [];
  for (const a of actions) {
    const hit = a.card_definition_id ? routable.get(a.card_definition_id) : undefined;
    if (hit && hit.event.action_types.includes(a.action_type)) {
      if (!emitted.has(a.card_definition_id!)) {
        emitted.add(a.card_definition_id!);
        steps.push({ kind: 'event', ...hit });
      }
      continue;
    }
    steps.push({ kind: 'action', action: a });
  }
  return steps;
}
